import { useCallback, useMemo, useState } from "react";
import { Button, Flex, Input, Modal, Switch } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { PERMISSIONS } from "../../../../constants/permission";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { IMasterData } from "../../../../interfaces/master-data";
import CurrencyList from "./list.currency";
import CreateAndEditCurrency from "./create-and-edit.currency";
import { debounce } from "../../../../utils/helper";

const CurrencyPage: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [titleModal, setTitleModal] = useState<string>("Add Currency");
  const [selectCurrency, setSelectCurrency] = useState<IMasterData>();
  const [okCurrencyTriggered, setOkCurrencyTriggered] = useState(true);
  const hasPermissionEditor = useHasPermission([PERMISSIONS.MASTER_DATA_EDITOR]);

  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  const [showActiveOnly, setShowActiveOnly] = useState<boolean>(true);

  const handleCreate = () => {
    setTitleModal("Add Currency");
    setSelectCurrency(undefined);
    setOpenModal(true);
  };

  const handleEditCurrency = (currency: IMasterData) => {
    setTitleModal("Edit Currency");
    setSelectCurrency(currency);
    setOpenModal(true);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
      }, 1000),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleChangeShowActiveOnly = useCallback(
    (checked: boolean) => {
      setShowActiveOnly(checked);
    },
    []
  );

  return (
    <div className="page-master-data">
      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Input
          value={searchValue}
          placeholder="Enter Currency Code or Currency Name"
          onChange={handleInputChange}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: "var(--text-blue-500)" }} />}
          style={{ width: 400 }}
          size="large"
          allowClear
        />
        <Flex gap={22} align="center">
          <div>
            <span style={{ marginRight: 6, alignItems: "center" }}>Show Active Only</span>
            <Switch checked={showActiveOnly} onChange={handleChangeShowActiveOnly} />
          </div>
          {hasPermissionEditor && (
            <Button type="primary" onClick={handleCreate}>
              <FontAwesomeIcon icon={faPlus} /> Add Currency
            </Button>
          )}
        </Flex>
      </Flex>

      <CurrencyList
        onEditCurrency={handleEditCurrency}
        trigger={okCurrencyTriggered}
        onDoneTrigger={() => setOkCurrencyTriggered(false)}
        debouncedSearchValue={debouncedSearchValue}
        showActiveOnly={showActiveOnly}
      />

      {openModal && (
        <Modal open={openModal} onCancel={() => setOpenModal(false)} footer={null} width={560} title={titleModal}>
          <CreateAndEditCurrency
            onClose={(isTrigger) => {
              setOpenModal(false);
              if (isTrigger) {
                setOkCurrencyTriggered((prev) => !prev);
              }
            }}
            masterData={selectCurrency}
          />
        </Modal>
      )}
    </div>
  );
};

export default CurrencyPage;
